import { useCallback } from 'react';
import { useFirebase } from '../contexts/FirebaseContext';
import { useError } from '../contexts/ErrorContext';

export function useApi() {
  const { getSecureHeaders } = useFirebase();
  const { logNetworkError } = useError();

  const callApi = useCallback(
    async (endpoint, method = 'GET', data = null) => {
      try {
        const headers = await getSecureHeaders();

        const options = {
          method,
          headers,
          // credentials: 'include', // Disabled for wildcard CORS
        };

        if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
          options.body = JSON.stringify(data);
        }

        // Use production Firebase Functions URL directly
        const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'wagl-c3119';
        const baseUrl = `https://us-central1-${projectId}.cloudfunctions.net/api`;
        const response = await fetch(`${baseUrl}${endpoint}`, options);

        if (!response.ok) {
          const errorData = await response.json();
          const error = new Error(`API error (${response.status}): ${errorData['error']}`);

          // Log network error with request details
          await logNetworkError(error, {
            endpoint,
            method,
            status_code: response.status,
            has_data: !!data,
            response_error: errorData['error'] || 'Unknown API error',
          });

          throw error;
        }

        return await response.json();
      } catch (error) {
        // Log network errors that aren't API errors (e.g., network failures, fetch failures)
        if (!error.message.includes('API error')) {
          await logNetworkError(error, {
            endpoint,
            method,
            has_data: !!data,
            error_type: 'network_failure',
          });
        }

        throw error;
      }
    },
    [getSecureHeaders, logNetworkError]
  );

  const get = useCallback((endpoint) => callApi(endpoint, 'GET'), [callApi]);
  const post = useCallback((endpoint, data) => callApi(endpoint, 'POST', data), [callApi]);
  const put = useCallback((endpoint, data) => callApi(endpoint, 'PUT', data), [callApi]);
  const patch = useCallback((endpoint, data) => callApi(endpoint, 'PATCH', data), [callApi]);
  const del = useCallback((endpoint) => callApi(endpoint, 'DELETE'), [callApi]);

  return {
    get,
    post,
    put,
    patch,
    delete: del,
  };
}
